import React from 'react';

import { CalendarMonth, Public, SdCard } from '@mui/icons-material';
import { Box, Chip, Divider, Grid, Paper, Skeleton, Stack, Typography, alpha, useTheme } from '@mui/material';

import { EMPTY_SOCIETE_ESIM_ORDER_SUMMARY, type SocieteEsimOrderSummaryModel } from './societeEsimOrderSummaryModel';

type Props = {
  /** Whether the order field is a valid UUID (order fetch may run). */
  orderQueryActive: boolean;
  isLoading: boolean;
  model: SocieteEsimOrderSummaryModel | null;
};

function SummaryField({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <Box sx={{ alignSelf: 'flex-start' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mt: 0.25, minHeight: 22 }}>
        {icon ? (
          <Box
            sx={{
              display: 'flex',
              color: 'text.secondary',
              flexShrink: 0,
              pt: 0.15,
              '& svg': { fontSize: 16 },
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {typeof value === 'string' ? (
            <Typography variant="body2" color="text.primary">
              {value}
            </Typography>
          ) : (
            value
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export default function SocieteEsimOrderSummaryCard({ orderQueryActive, isLoading, model }: Props) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const borderSoft = alpha(theme.palette.divider, 0.12);

  const showLoadingShell = orderQueryActive && isLoading;
  const displayModel = model ?? EMPTY_SOCIETE_ESIM_ORDER_SUMMARY;

  const nameContent = showLoadingShell ? (
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      <Skeleton variant="text" width="55%" height={26} />
      <Skeleton variant="text" width="75%" height={20} />
      <Skeleton variant="text" width="100%" height={20} />
    </Stack>
  ) : (
    <>
      <Typography variant="subtitle1" fontWeight={700} title={displayModel.customerName}>
        {displayModel.customerName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.125 }} title={displayModel.customerEmail}>
        {displayModel.customerEmail}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.25, wordBreak: 'break-all', lineHeight: 1.35 }}
        title={displayModel.customerIdFull}
      >
        {displayModel.customerIdFull}
      </Typography>
    </>
  );

  const tierContent = showLoadingShell ? (
    <Skeleton variant="rounded" width={84} height={26} sx={{ flexShrink: 0, borderRadius: 999 }} />
  ) : displayModel.tierBadgeLabel ? (
    <Chip
      label={displayModel.tierBadgeLabel}
      size="small"
      sx={{
        fontWeight: 700,
        bgcolor: 'grey.900',
        color: 'common.white',
        borderRadius: 999,
        flexShrink: 0,
        height: 26,
      }}
    />
  ) : (
    <Chip
      label="—"
      size="small"
      variant="outlined"
      sx={{
        flexShrink: 0,
        borderRadius: 999,
        height: 26,
        color: 'text.secondary',
        borderColor: alpha(theme.palette.divider, 0.35),
      }}
    />
  );

  const fieldValue = (text: string) => (showLoadingShell ? <Skeleton variant="text" width="70%" height={20} /> : text);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${borderSoft}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        height: '100%',
        minHeight: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1,
          borderBottom: `1px solid ${borderSoft}`,
          bgcolor: alpha(accent, 0.06),
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
          Order summary
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textAlign: 'right', flexShrink: 0 }}
        >
          Step 2 · review before generating
        </Typography>
      </Box>

      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack spacing={1} sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
            <Box flex={1} minWidth={0}>
              {nameContent}
            </Box>
            <Box sx={{ lineHeight: 0 }}>{tierContent}</Box>
          </Stack>

          <Divider sx={{ borderColor: borderSoft, my: 0 }} />

          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid size={{ xs: 12, sm: 6 }}>
              <SummaryField label="Booking" value={fieldValue(displayModel.bookingLine)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SummaryField
                label="Trip country"
                value={fieldValue(displayModel.tripCountryLine)}
                icon={<Public fontSize="inherit" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SummaryField
                label="Locale"
                value={fieldValue(displayModel.localeDisplay)}
                icon={<Public fontSize="inherit" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SummaryField
                label="Departure"
                value={fieldValue(displayModel.departureDisplay)}
                icon={<CalendarMonth fontSize="inherit" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SummaryField
                label="Package"
                value={fieldValue(displayModel.packageLine)}
                icon={<SdCard fontSize="inherit" />}
              />
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </Paper>
  );
}
